/**
 * How one key moment is shown — shared by the "Errors & Key Moments" card, the
 * attention-flag block and the alert card's moment (#2002), so the moment under
 * a flag reads the same wherever it is shown: one place decides the speaker
 * tag and whether the time is shown.
 */

import type { TFunction } from 'i18next';
import { isTimecodeBeyondDuration, parseTimecode } from '@aica/core';

interface KeyMomentInput {
  timestamp?: string | null;
  label: string;
  quote: string;
  speaker?: 'agent' | 'customer' | 'unknown';
}

export interface KeyMomentView {
  time?: string;
  startSeconds?: number;
  /** `[Agent]` / `[Client]` / `[Role unknown]`; none when the moment names no speaker. */
  speakerTag?: string;
  label: string;
  quote: string;
  /** `[Speaker] label: "quote"`; an empty quote leaves no quotes and no colon. */
  text: string;
}

function speakerTagOf(
  t: TFunction,
  speaker: KeyMomentInput['speaker']
): string | undefined {
  if (!speaker) return undefined;
  return speaker === 'agent'
    ? t('records.analysisPanel.speakerShortAgent', '[Agent]')
    : speaker === 'customer'
      ? t('records.analysisPanel.speakerShortClient', '[Client]')
      : t('records.analysisPanel.speakerShortUnknown', '[Role unknown]');
}

export function describeKeyMoment(
  moment: KeyMomentInput,
  t: TFunction,
  durationSeconds?: number
): KeyMomentView {
  // UI-side defence in depth (H-5): if a hallucinated timestamp beyond the
  // record's own duration ever reaches this component unclamped, hide
  // just the timecode — the moment's text is usually still a real,
  // useful observation.
  const seconds = parseTimecode(moment.timestamp);
  const outOfRange = isTimecodeBeyondDuration(seconds, durationSeconds);
  const speakerTag = speakerTagOf(t, moment.speaker);
  const prefix = speakerTag ? `${speakerTag} ` : '';
  return {
    time: outOfRange ? undefined : (moment.timestamp ?? undefined),
    startSeconds: outOfRange ? undefined : (seconds ?? undefined),
    speakerTag,
    label: moment.label,
    quote: moment.quote,
    text: moment.quote
      ? `${prefix}${moment.label}: "${moment.quote}"`
      : `${prefix}${moment.label}`,
  };
}
